/**
 * store/favorites.ts — the «избранное» slice: which tracks, and since task 77 which clips, carry
 * a heart.
 *
 * Task 11. `spec.md` story 57: «добавлять трек в избранное одним касанием». The heart is on
 * every list row, on the mini player and on «Сейчас играет», and all of them read the one set
 * here — so a heart put on in one place is on in every other at once, and no list keeps a copy
 * that could disagree. Its own store, like the other slices: a heart tapped re-renders the
 * buttons whose answer changed and nothing that draws a list or the player — but «Избранные» of
 * «Видео», the one list made of hearts, when a heart of its clips changes.
 *
 * Task 77: a clip's heart is in the same set, under the clip's id — on its row of «Видео» and on
 * the key «В избранном» under the frame. «Моё» lists tracks only (`spec.md` «Моё»); the clips
 * with a heart are the «Избранные» of «Видео» (`store/video.ts`).
 *
 * ## The heart answers the finger, the server follows
 *
 * The checklist: «отклик мгновенный, сервер подтверждает следом». A tap changes the set at
 * once and then asks the server. If the server does not take it, the heart goes back to what
 * the server has, and {@link FavoritesState.failed} says so (`components/FavoriteNotice.tsx`).
 *
 * ## One request per track at a time, and the last tap wins
 *
 * Three taps on a slow connection are «on, off, on». Sent as three requests, they could land in
 * any order and leave the server with «off». So a track has at most one request out; a tap
 * while it is out only changes the set, and when the answer comes the set is compared with what
 * the server now has — if they differ, one more request goes, with the set's value. The server
 * ends where the listener's last tap did, and the taps in between cost nothing.
 *
 * ## The hearts of the server are read at launch
 *
 * `app/Shell.tsx` asks for them (`GET /api/favorites/ids`) when the listener is in, and
 * «Моё» asks again if that failed. A heart tapped while the answer is on its way is not
 * overwritten by it: the answer may have been read before the tap reached the server.
 *
 * Task 77: «Видео» asks again on every entry. Its «Избранные» are the server's list of the
 * clips with a heart, and a heart put on a clip on another phone since the launch must be drawn
 * there, and play.
 */

import { create } from 'zustand';

import { fetchFavoriteIds, saveFavorite } from '../lib/api';

import { useSession } from './session';

export interface FavoritesState {
  /** The tracks and, since task 77, the clips with a heart, as the screen draws them. */
  readonly ids: ReadonlySet<number>;
  /** The server's hearts have been read. Until then the set holds only this launch's taps. */
  readonly loaded: boolean;
  /** A save did not go through: the heart went back, and the notice says why. */
  readonly failed: boolean;

  /** A tap on a heart. */
  readonly toggle: (trackId: number) => void;
  /** Reads the server's hearts, unless they are being read already. */
  readonly load: () => Promise<void>;
  /** The listener read the notice. */
  readonly dismiss: () => void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Module state: nothing here is drawn, so it is not in the store
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * What the server has for every track with a change not yet settled: the value before the
 * first tap, then whatever the server confirmed. A track settles — leaves this map — once the
 * server has what the set shows.
 */
const confirmed = new Map<number, boolean>();
/** Tracks with a request out. */
const sending = new Set<number>();
/** Tracks the server confirmed while the launch read was out: the read may predate them. */
const writtenDuringLoad = new Set<number>();
let loading: Promise<void> | undefined;
/** Bumped by {@link resetFavorites}: an answer of the page before a reset changes nothing. */
let epoch = 0;

function token(): string | undefined {
  return useSession.getState().session?.token;
}

function withHeart(ids: ReadonlySet<number>, trackId: number, on: boolean): ReadonlySet<number> {
  if (ids.has(trackId) === on) return ids;
  const next = new Set(ids);
  if (on) next.add(trackId);
  else next.delete(trackId);
  return next;
}

const INITIAL = Object.freeze({ ids: new Set<number>(), loaded: false, failed: false });

export const useFavorites = create<FavoritesState>((set, get) => {
  /** Sends what the set shows for `trackId`, if the server does not have it and nothing is out. */
  const settle = async (trackId: number): Promise<void> => {
    if (sending.has(trackId)) return;
    const known = confirmed.get(trackId);
    if (known === undefined) return;
    const wanted = get().ids.has(trackId);
    if (known === wanted) {
      confirmed.delete(trackId);
      return;
    }

    const mine = epoch;
    sending.add(trackId);
    const result = await saveFavorite(token(), trackId, wanted);
    if (mine !== epoch) return;
    sending.delete(trackId);

    if (result.ok) {
      confirmed.set(trackId, wanted);
      if (loading !== undefined) writtenDuringLoad.add(trackId);
      // The listener may have tapped again while this was out.
      await settle(trackId);
      return;
    }
    // The heart goes back to what the server has, whatever was tapped since: the connection
    // that just failed would fail the next request too, and a heart that says «saved» when
    // nothing is saved is worse than one that says «try again». A tap since that already put
    // it back leaves nothing to say: the listener ended where they wanted.
    confirmed.delete(trackId);
    if (get().ids.has(trackId) !== known) {
      set({ ids: withHeart(get().ids, trackId, known), failed: true });
    }
  };

  return {
    ...INITIAL,

    toggle: (trackId) => {
      const on = !get().ids.has(trackId);
      if (!confirmed.has(trackId)) confirmed.set(trackId, !on);
      set({ ids: withHeart(get().ids, trackId, on), failed: false });
      void settle(trackId);
    },

    load: async () => {
      if (loading !== undefined) {
        await loading;
        return;
      }
      const mine = epoch;
      writtenDuringLoad.clear();
      const attempt = (async (): Promise<void> => {
        const result = await fetchFavoriteIds(token());
        if (mine !== epoch || !result.ok || !Array.isArray(result.data.ids)) return;
        const next = new Set(
          result.data.ids.filter((id): id is number => Number.isInteger(id) && id > 0),
        );
        // A heart with a change of this launch keeps what the screen shows: the server either
        // has it already or is about to.
        const local = get().ids;
        for (const id of [...confirmed.keys(), ...writtenDuringLoad]) {
          if (local.has(id)) next.add(id);
          else next.delete(id);
        }
        set({ ids: next, loaded: true });
      })();
      loading = attempt;
      try {
        await attempt;
      } finally {
        if (mine === epoch) {
          loading = undefined;
          writtenDuringLoad.clear();
        }
      }
    },

    dismiss: () => {
      if (get().failed) set({ failed: false });
    },
  };
});

/**
 * Back to no hearts, with nothing in flight counted. For tests: the store is module-level and
 * outlives every shell a test renders, and a late answer of one test must not land in the next.
 */
export function resetFavorites(): void {
  epoch += 1;
  confirmed.clear();
  sending.clear();
  writtenDuringLoad.clear();
  loading = undefined;
  useFavorites.setState(INITIAL);
}
